// ---------------------------------------------------------------------------
// ADS-010 Phase 2 — CAPI server-side dispatch CQRS event catalog + persistence
// ---------------------------------------------------------------------------
// Story ADS-010: "CAPI/server-side events send consent-safe conversion events
// without duplicate browser/server counting."
//
// This file catalogues the 3 dispatcher-side CQRS event types and the
// per-tenant idempotency table `capi_dispatches` consumed by the Phase 3
// dispatcher service at `packages/marketing/src/services/capi-dispatcher.ts`.
//
// What this file is (per Governance Rule #7 — Event Catalog):
// - The single source of truth for the `analytics.conversion_dispatched@v1`,
//   `analytics.conversion_deduped@v1`, and `analytics.conversion_consent_blocked@v1`
//   event-type strings.
// - Typed payload contracts so the dispatcher + queue consumer + audit-runner
//   cannot drift on shape.
// - The closed `block_reason` and `dedup_source` vocabularies returned by the
//   dispatcher.
// - The `capi_dispatches` table: per-(tenant, platform, idempotency_key)
//   record used by the dispatcher to short-circuit duplicate browser-pixel
//   `event_id`s. UNIQUE(tenant_id, platform, idempotency_key) guarantees
//   `INSERT ... ON CONFLICT DO NOTHING` is the canonical idempotency guard
//   (Security Drift Prevention rule: "MUST use INSERT ... ON CONFLICT DO
//   NOTHING RETURNING *. Never read-then-write (TOCTOU).").
//
// What this file is NOT:
// - It does NOT replace AN-027's consent-suppression catalog. AN-027 emits
//   `analytics.capi_suppressed_by_consent@v*` (suppression-stage events for
//   route entry / dispatch time). ADS-010 emits a single dispatcher-side
//   `analytics.conversion_consent_blocked@v1` that the dispatcher fires when
//   its `hasConsent` dep returns false. Both vocabularies coexist because
//   they observe consent at different layers.
// - It does NOT log raw PII. The dispatcher's user-data fields are SHA-256
//   hashed before they reach the adapter (see `normalizeAndHashIdentifiers`
//   in the service). The CQRS payload only stores hashed identifiers'
//   presence flags — never the hashes themselves.
// ---------------------------------------------------------------------------

import {
  pgTable,
  uuid,
  text,
  timestamp,
  integer,
  uniqueIndex,
  index,
  check,
} from "drizzle-orm/pg-core";
import { sql } from "drizzle-orm";

const tz = { withTimezone: true } as const;

// ---------------------------------------------------------------------------
// Closed vocabularies
// ---------------------------------------------------------------------------

/** Closed CAPI platform vocabulary supported by the dispatcher. */
export const ADS_010_CAPI_DISPATCH_PLATFORMS = ["meta", "google"] as const;
export type Ads010CapiDispatchPlatform = (typeof ADS_010_CAPI_DISPATCH_PLATFORMS)[number];

/**
 * Closed dispatcher block-reason vocabulary. Returned in the
 * `conversion_consent_blocked@v1` payload so audit + DSAR replay can group
 * suppressions by cause without joining `platform_consent_records`.
 *
 * - `consent_denied`: the consent dep returned false for this tenant + subject.
 * - `consent_dep_unavailable`: the consent dep raised — dispatcher fails
 *   closed (no PII leaves the boundary on a consent-system fault).
 */
export const ADS_010_BLOCK_REASONS = [
  "consent_denied",
  "consent_dep_unavailable",
] as const;
export type Ads010BlockReason = (typeof ADS_010_BLOCK_REASONS)[number];

/**
 * Closed dedup-source vocabulary. `browser_pixel` means the same
 * `idempotency_key` was already recorded (likely from the browser pixel
 * dispatch). `server_replay` means the dispatcher itself was re-invoked
 * with the same key (queue retry, exactly-once safety).
 */
export const ADS_010_DEDUP_SOURCES = ["browser_pixel", "server_replay"] as const;
export type Ads010DedupSource = (typeof ADS_010_DEDUP_SOURCES)[number];

/**
 * Closed catalog of the 3 ADS-010 dispatcher CQRS event types. Versioned
 * with `@v1` per platform convention so future shape changes ship `@v2`
 * alongside (never mutate in place).
 *
 * One business fact per event (Governance Rule #6):
 * - `dispatched`: the dispatcher accepted the conversion, recorded a row
 *   in `capi_dispatches`, and forwarded it to the platform adapter.
 * - `deduped`: a row already exists for (tenant, platform, idempotency_key);
 *   the dispatcher short-circuited and did not call the adapter.
 * - `consent_blocked`: the consent dep returned false (or raised); the
 *   dispatcher did not call the adapter and did not record a dispatches row.
 */
export const ADS_010_DISPATCH_EVENT_TYPES = [
  "analytics.conversion_dispatched@v1",
  "analytics.conversion_deduped@v1",
  "analytics.conversion_consent_blocked@v1",
] as const;

export type Ads010DispatchEventType =
  (typeof ADS_010_DISPATCH_EVENT_TYPES)[number];

/** Runtime narrowing guard for DLQ + audit-runner boundaries. */
export function isAds010DispatchEventType(
  value: unknown,
): value is Ads010DispatchEventType {
  return (
    typeof value === "string" &&
    (ADS_010_DISPATCH_EVENT_TYPES as readonly string[]).includes(value)
  );
}

// ---------------------------------------------------------------------------
// Per-event payload contracts (typed, drift-proof)
// ---------------------------------------------------------------------------

interface Ads010DispatchBasePayload {
  /** Tenant scope. */
  tenantId: string;
  /** Closed CAPI platform vocabulary. */
  platform: Ads010CapiDispatchPlatform;
  /**
   * Shared browser-pixel ↔ CAPI deduplication key. Same as Meta `event_id`
   * + Google `orderId`. The dispatcher does NOT default-generate this —
   * empty string fails loudly (avoids accidental cross-conversion collisions).
   */
  idempotencyKey: string;
  /** Canonical platform-agnostic event name (e.g. "Purchase", "Lead"). */
  eventName: string;
}

export interface Ads010ConversionDispatchedPayload
  extends Ads010DispatchBasePayload {
  /** ISO-8601 when the dispatcher accepted the conversion. */
  dispatchedAt: string;
  /**
   * Which hashed identifier classes were sent — presence flags only.
   * Never the hashes themselves (audit posture: minimize hashed-PII fanout).
   */
  hashedIdentifierClasses: {
    email: boolean;
    phone: boolean;
    externalId: boolean;
  };
  /** Conversion value in cents, when present. */
  valueCents: number | null;
  /** ISO 4217 currency code, when present. */
  currency: string | null;
  /** Platform-returned event id (Meta `events_received` id / Google order id). */
  platformEventId: string | null;
}

export interface Ads010ConversionDedupedPayload
  extends Ads010DispatchBasePayload {
  /** ISO-8601 when the dispatcher observed the duplicate. */
  observedAt: string;
  /** Why the dispatcher short-circuited. */
  dedupSource: Ads010DedupSource;
  /** ISO-8601 of the original dispatch row (`capi_dispatches.created_at`). */
  originalDispatchedAt: string;
}

export interface Ads010ConversionConsentBlockedPayload
  extends Ads010DispatchBasePayload {
  /** ISO-8601 when the dispatcher refused to call the adapter. */
  blockedAt: string;
  /** Why the dispatcher blocked. */
  blockReason: Ads010BlockReason;
}

/**
 * Discriminated union mapping each event type to its payload. Consumers
 * (dispatcher emitter, audit-runner, DLQ) can `switch (event.type)` and
 * pull the right payload shape without any-casting.
 */
export type Ads010DispatchEvent =
  | {
      type: "analytics.conversion_dispatched@v1";
      payload: Ads010ConversionDispatchedPayload;
    }
  | {
      type: "analytics.conversion_deduped@v1";
      payload: Ads010ConversionDedupedPayload;
    }
  | {
      type: "analytics.conversion_consent_blocked@v1";
      payload: Ads010ConversionConsentBlockedPayload;
    };

// ---------------------------------------------------------------------------
// `capi_dispatches` — per-tenant idempotency table
// ---------------------------------------------------------------------------
// One row per accepted dispatch. UNIQUE(tenant_id, platform, idempotency_key)
// is the canonical idempotency guard. The dispatcher writes via
// `INSERT ... ON CONFLICT DO NOTHING RETURNING *` so duplicate browser-pixel
// keys (or queue retries) return zero rows and the dispatcher emits
// `conversion_deduped@v1` without calling the adapter.
//
// Hashed-identifier presence flags ONLY — never the hashes themselves.
// Per Stero security posture: hashed PII lives in `capi_event_log` (delivery
// telemetry), not in the dispatcher idempotency table.

export const capiDispatches = pgTable(
  "capi_dispatches",
  {
    id: uuid("id").primaryKey().defaultRandom(),
    tenantId: uuid("tenant_id").notNull(),
    /** Closed: 'meta' | 'google'. CHECK enforced below. */
    platform: text("platform").notNull(),
    /** Shared browser-pixel ↔ CAPI dedup key. Non-empty CHECK enforced. */
    idempotencyKey: text("idempotency_key").notNull(),
    /** Canonical event name (e.g. "Purchase"). */
    eventName: text("event_name").notNull(),
    /** Conversion value in cents (nullable for non-monetary events). */
    valueCents: integer("value_cents"),
    /** ISO 4217 currency code. */
    currency: text("currency"),
    /** Presence flags ONLY — no hashes here. */
    hadHashedEmail: text("had_hashed_email").notNull().default("false"),
    hadHashedPhone: text("had_hashed_phone").notNull().default("false"),
    hadExternalId: text("had_external_id").notNull().default("false"),
    /** Platform-returned event id when available. */
    platformEventId: text("platform_event_id"),
    createdAt: timestamp("created_at", tz).notNull().defaultNow(),
  },
  (table) => [
    uniqueIndex("uq_capi_dispatches_tenant_platform_key").on(
      table.tenantId,
      table.platform,
      table.idempotencyKey,
    ),
    index("idx_capi_dispatches_tenant_created").on(
      table.tenantId,
      table.createdAt,
    ),
    check(
      "chk_capi_dispatches_platform",
      sql`${table.platform} IN ('meta', 'google')`,
    ),
    check(
      "chk_capi_dispatches_key_nonempty",
      sql`length(${table.idempotencyKey}) > 0`,
    ),
    check(
      "chk_capi_dispatches_flag_email",
      sql`${table.hadHashedEmail} IN ('true', 'false')`,
    ),
    check(
      "chk_capi_dispatches_flag_phone",
      sql`${table.hadHashedPhone} IN ('true', 'false')`,
    ),
    check(
      "chk_capi_dispatches_flag_external",
      sql`${table.hadExternalId} IN ('true', 'false')`,
    ),
  ],
);
